/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { BaseFeatureMock } from '../mockServer/featureMock';
import {
    AlertDto,
    AlertsListDto,
    AlertsSearchParams,
    AlertItemsSearchParams,
    AlertsSortType,
    AlertItemsListDto,
    AlertItemDto,
    AlertTemplateDTO,
    TemplatesListSearchParams,
    TemplatesList,
    TemplatesListSortType,
    ALERT_NAME,
    TemplateExecutionListDTO,
    TemplateRuleDto,
    CreateTemplateRulesParams,
    CreateTemplateRuleDto,
    TemplateRulesListDto,
    TemplateRulesListSearchParams,
    UpdateTemplateRuleDto,
    RuleType,
    TemplateRuleEntityType,
    TemplateExecutionsSearchParams,
} from './alertsClient.typings';
import {
    alertItemsDtoFactory,
    alertDtoFactory,
    alertItemContentFactoryByType,
    alertTemplateFactory,
    alertTemplateExecution,
    alertTemplateRuleFactory,
} from './alertFactories';
import { Observable, Subscription, interval } from 'rxjs';
import { tap, mapTo } from 'rxjs/operators';
import moment from 'moment';
import { sortByProp } from '../../../utils/utils';
import { sample } from 'lodash';

const ALERTS_URL = /\/alerts$/;
const ALERT_ITEMS_URL = /\/alerts\/(\d+)\/items/;
const UPDATE_ALERTS_URL = /\/alerts$/;
const GET_ALERT_DETAILS_URL = /\/alerts\/\d+/;
const UPDATE_ALERT_ITEM_URL = /\/alerts\/\d+\/items\/\d+/;
const ALERTS_TEMPLATES_URL = /\/alerts\/templates/;
const UPDATE_ALERTS_TEMPLATES_URL = /\/alerts\/templates\/\d+/;
const GET_EXECUTIONS_LIST = /\/alerts\/templates\/\d+\/executions/;
const TEMPLATE_RULES_URL = /\/alerts\/types(\/?$|\/(?!taxNumbersList).*)/;
const TEMPLATE_RULES_TAX_NUMBERS_LIST_URL = /\/alerts\/types\/taxNumbersList$/;

const ALERTS_TOTAL = 60;
const ALERTS_TEMPLATES_TOTAL = ALERT_NAME.length;
const ALERTS_TEMPLATE_RULES_TOTAL = 40;
const NEW_ALERT_EVERY_MS = 20000;

export class AlertsBackendStore {
    private alerts: AlertDto[] = [];
    private newAlertCreatedTime: moment.Moment = moment('2021-08-30T00:00:00.000Z');

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            this.createAlert();
        }
    }

    public getAlerts(params: AlertsSearchParams): AlertsListDto {
        let alerts = this.alerts.slice();

        // simple sorting
        if ((params.sort as unknown as AlertsSortType[]).includes('inserted,desc')) {
            alerts.reverse();
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('status,desc')) {
            alerts = sortByProp(alerts, 'status');
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('assigneeUserId,desc')) {
            alerts = alerts.sort((a, b) => a.assigneeUser.id - b.assigneeUser.id);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('assigneeUserId,asc')) {
            alerts = alerts.sort((a, b) => b.assigneeUser.id - a.assigneeUser.id);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('itemsCount,desc')) {
            alerts = alerts.sort((a, b) => a.itemsCount - b.itemsCount);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('itemsCount,asc')) {
            alerts = alerts.sort((a, b) => b.itemsCount - a.itemsCount);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('type.priority,asc')) {
            alerts.reverse();
        }

        if (
            (params.sort as unknown as AlertsSortType[]).includes('id,asc') &&
            (params.sort as unknown as AlertsSortType[]).length === 1
        ) {
            alerts.reverse();
        }

        // simple filtering

        if (params.dateFrom) {
            alerts = alerts.filter((alert) => {
                return moment(alert.inserted).isAfter(params.dateFrom);
            });
        }

        if (params.status.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.status.includes(alert.status);
            });
        }

        if (params.priority.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.priority.includes(alert.type.priority);
            });
        }

        if (params.type.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.type.includes(alert.type.name);
            });
        }

        if (params.assigneeUserId.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.assigneeUserId.includes(alert.assigneeUser.id);
            });
        }

        const offset = params.offset * params.limit;

        return {
            count: alerts.length,
            alerts: alerts.slice(offset, offset + params.limit).map((alert) => ({ ...alert })),
        };
    }

    public getAlert(id: AlertDto['id']): AlertDto {
        const alert = this.alerts.find((a) => a.id === id);

        return alert && { ...alert };
    }

    public updateAlerts(alerts: AlertDto[]) {
        for (let i = 0; i < alerts.length; i++) {
            const index = this.alerts.findIndex((item) => item.id === alerts[i].id);

            if (index >= 0) {
                this.alerts.splice(index, 1, alerts[i]);
            }
        }

        return alerts;
    }

    public initAlertsCreationTask(createAlertEveryMs: number): Observable<null> {
        return interval(createAlertEveryMs).pipe(
            tap(() => {
                this.createAlert();
            }),
            mapTo(null),
        );
    }

    private createAlert(): void {
        const alert = alertDtoFactory.item({
            inserted: this.newAlertCreatedTime.valueOf(),
        });

        this.newAlertCreatedTime.add(1, 'h');
        this.alerts.push(alert);
    }
}

export class AlertsTemplatesBackendStore {
    private templates: AlertTemplateDTO[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            this.createAlertTemplate();
        }
    }

    public getTemplates(params: TemplatesListSearchParams): TemplatesList {
        let templates = this.templates.slice();

        if ((params.sort as unknown as TemplatesListSortType[]).includes('id,asc')) {
            templates = templates.sort((a, b) => a.id - b.id);
        }

        const offset = params.offset * params.limit;

        return {
            count: templates.length,
            alertsTemplates: templates.slice(offset, offset + params.limit).map((template) => ({ ...template })),
        };
    }

    private createAlertTemplate(): void {
        const template = alertTemplateFactory.item();

        this.templates.push(template);
    }
}

export class AlertsTemplateRulesBackendStore {
    private rules: TemplateRuleDto[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            this.createAlertTemplateRule({
                ruleType: sample(Object.values(RuleType)),
                ruleEntityType: sample(Object.values(TemplateRuleEntityType)),
                taxNumber: `1005060${Math.floor(Math.random() * 120)}`,
            });
        }
    }

    private createAlertTemplateRule(rule: CreateTemplateRuleDto): void {
        const newRule: Pick<TemplateRuleDto, 'id' | 'insertedAtUtc'> = alertTemplateRuleFactory.item();

        this.rules.unshift({ ...newRule, ...rule });
    }

    createAlertTemplateRules(params: string): void {
        const data: CreateTemplateRulesParams = JSON.parse(params);
        if (data.hasOwnProperty('clear') && data.clear) {
            this.rules = [];
        }

        data.rules.map((rule) => {
            this.createAlertTemplateRule({
                ruleType: rule.ruleType,
                ruleEntityType: rule.ruleEntityType,
                taxNumber: rule.taxNumber,
            });
        });
    }

    getAlertTemplateRules(params: TemplateRulesListSearchParams): TemplateRulesListDto {
        let rules = this.rules.slice();
        const offset = params.offset * params.limit;

        if (params.ruleType) {
            rules = rules.filter((rule) => {
                return params.ruleType === rule.ruleType;
            });
        }

        if (params.ruleEntityType) {
            rules = rules.filter((rule) => {
                return params.ruleEntityType === rule.ruleEntityType;
            });
        }

        if (params.taxNumber) {
            const searchString = params.taxNumber.toLowerCase().trim();

            rules = rules.filter((rule) => rule.taxNumber.toLowerCase().includes(searchString));
        }

        return {
            count: rules.length,
            rules: rules.slice(offset, offset + params.limit).map((rule) => ({ ...rule })),
        };
    }

    deleteTemplateRules(rulesIds: string, all: boolean = false): void {
        const ids: number[] = JSON.parse(rulesIds);

        if (!all) {
            ids.map((id) => {
                const index = this.rules.findIndex((rule) => rule.id === id);

                if (index !== -1) {
                    this.rules.splice(index, 1);
                }
            });
        } else {
            this.rules = [];
        }
    }

    updateTemplateRule(id: number, params: string): void {
        const data: UpdateTemplateRuleDto = JSON.parse(params);

        this.rules = this.rules.reduce((acc, rule) => {
            if (rule.id === id) {
                return [...acc, { ...rule, ruleType: data.ruleType }];
            }

            return [...acc, rule];
        }, []);
    }
}

class AlertsFeatureMock extends BaseFeatureMock {
    private alertsBackend = new AlertsBackendStore({ total: ALERTS_TOTAL });
    private alertsTemplatesBackend = new AlertsTemplatesBackendStore({ total: ALERTS_TEMPLATES_TOTAL });
    private alertsTemplateRulesBackend = new AlertsTemplateRulesBackendStore({ total: ALERTS_TEMPLATE_RULES_TOTAL });
    private periodicCreationSubscription: Subscription | null = null;

    public readonly alertsList = this.route<AlertsListDto>({
        method: 'get',
        url: ALERTS_URL,
        defaultResponse: (config) => {
            const data = this.alertsBackend.getAlerts(config.params as AlertsSearchParams);

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly alertItems = this.route<AlertItemsListDto>({
        method: 'get',
        url: ALERT_ITEMS_URL,
        defaultResponse: (config) => {
            const alertId = parseInt(config.url.match(ALERT_ITEMS_URL)[1]);
            const alertDto = this.alertsBackend.getAlert(alertId);
            const code = alertDto ? 200 : 404;
            const params = config.params as AlertItemsSearchParams;
            const count = alertDto.itemsCount;

            const offset = params.offset * params.limit;
            const items = alertItemsDtoFactory
                .list({ count })
                .map((item) => {
                    item.content = alertItemContentFactoryByType[alertDto.type.entityType].item();
                    return item;
                })
                .slice(offset, offset + params.limit);

            const data: AlertItemsListDto = { count, items };

            return [code, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly updateAlertItem = this.route<AlertItemDto, AlertItemDto>({
        method: 'put',
        url: UPDATE_ALERT_ITEM_URL,
        defaultResponse: (config) => {
            const alertItemDto = JSON.parse(config.data);

            return [200, alertItemDto];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly executionsList = this.route<TemplateExecutionListDTO>({
        method: 'get',
        url: GET_EXECUTIONS_LIST,
        defaultResponse: (config) => {
            const params = config.params as TemplateExecutionsSearchParams;
            const executionsCount = 35;
            const offset = params.offset * params.limit;

            const data: TemplateExecutionListDTO = {
                count: executionsCount,
                alertsChecksExecutions: alertTemplateExecution
                    .list({ count: executionsCount })
                    .slice(offset, offset + params.limit),
            };

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly updateAlerts = this.route<AlertDto[], AlertDto[]>({
        method: 'post',
        url: UPDATE_ALERTS_URL,
        defaultResponse: (config) => {
            const data = this.alertsBackend.updateAlerts(JSON.parse(config.data));

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly alertDetails = this.route<AlertDto>({
        method: 'get',
        url: GET_ALERT_DETAILS_URL,
        defaultResponse: (config) => {
            const alertId = parseInt(config.url.split('/')[2]);
            const data = this.alertsBackend.getAlert(alertId);

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly templates = this.route<TemplatesList>({
        method: 'get',
        url: ALERTS_TEMPLATES_URL,
        defaultResponse: (config) => {
            const data = this.alertsTemplatesBackend.getTemplates(config.params as TemplatesListSearchParams);

            return [200, data];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly updateTemplate = this.route<AlertTemplateDTO, AlertTemplateDTO>({
        method: 'post',
        url: UPDATE_ALERTS_TEMPLATES_URL,
        defaultResponse: (config) => {
            const templateDto = JSON.parse(config.data);

            return [200, templateDto];
        },
        delayMs: () => Math.random() * 100,
    });

    public readonly templateRules = this.route<TemplateRulesListDto>({
        method: 'get',
        url: TEMPLATE_RULES_URL,
        defaultResponse: (config) => {
            const data = this.alertsTemplateRulesBackend.getAlertTemplateRules(
                config.params as TemplateRulesListSearchParams,
            );

            return [200, data];
        },
        delayMs: () => Math.random() * 2000,
    });

    public readonly createTemplateRules = this.route<{}, CreateTemplateRulesParams>({
        method: 'post',
        url: TEMPLATE_RULES_URL,
        defaultResponse: (config) => {
            this.alertsTemplateRulesBackend.createAlertTemplateRules(config.data);

            return [200, {}];
        },
        delayMs: () => Math.random() * 2000,
    });

    public readonly updateTemplateRule = this.route<{}, UpdateTemplateRuleDto>({
        method: 'patch',
        url: TEMPLATE_RULES_URL,
        defaultResponse: (config) => {
            const ruleId = +config.url.split('/')[5];

            this.alertsTemplateRulesBackend.updateTemplateRule(ruleId, config.data);

            return [200, {}];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly deleteTemplateRules = this.route<{}, number[]>({
        method: 'delete',
        url: TEMPLATE_RULES_URL,
        defaultResponse: (config) => {
            this.alertsTemplateRulesBackend.deleteTemplateRules(config.data, false);

            return [200, {}];
        },
        delayMs: () => Math.random() * 1000,
    });

    public readonly taxNumbersList = this.route<string[]>({
        method: 'post',
        url: TEMPLATE_RULES_TAX_NUMBERS_LIST_URL,
        defaultResponse: (config) => {
            const body = JSON.parse(config.data);
            const arr = Array(body.limit).fill(0);
            const data = arr.map(() => body.taxNumber + (Math.random() * 100000).toFixed());

            return [200, data];
        },
        delayMs: () => Math.random() * 1000,
    });

    /**
     * Only dev-mode registration (`{ delay: true }`, from `addAlertsMockApi`) starts the
     * periodic new-alert creation task, matching the old mock's always-on behavior in that
     * mode. Tests register with `{ delay: false }` and never see this timer - a real
     * `setInterval` ticking in the background across a test file's whole run is exactly the
     * kind of latent flakiness/leak source this migration is meant to avoid, and no
     * integration test needs alerts to arrive spontaneously (see the deterministic
     * `newAlertsInfo`-via-`preloadedState` approach used instead).
     */
    public register(adapter: MockAdapter, opts: { delay?: boolean } = {}): void {
        super.register(adapter, opts);

        if (opts.delay && !this.periodicCreationSubscription) {
            this.periodicCreationSubscription = this.alertsBackend
                .initAlertsCreationTask(NEW_ALERT_EVERY_MS)
                .subscribe();
        }
    }

    protected onReset(): void {
        this.periodicCreationSubscription?.unsubscribe();
        this.periodicCreationSubscription = null;

        this.alertsBackend = new AlertsBackendStore({ total: ALERTS_TOTAL });
        this.alertsTemplatesBackend = new AlertsTemplatesBackendStore({ total: ALERTS_TEMPLATES_TOTAL });
        this.alertsTemplateRulesBackend = new AlertsTemplateRulesBackendStore({ total: ALERTS_TEMPLATE_RULES_TOTAL });

        alertDtoFactory.resetCount();
        alertItemsDtoFactory.resetCount();
        alertTemplateFactory.resetCount();
        alertTemplateExecution.resetCount();
        alertTemplateRuleFactory.resetCount();
    }
}

export const alertsMock = new AlertsFeatureMock();

/** Backward-compat shim - dev mock mode (mockAdapter.ts) keeps calling this untouched. */
export function addAlertsMockApi(mockAdapter: MockAdapter): void {
    alertsMock.register(mockAdapter, { delay: true });
}
